import { type Home, type ResidentDtos, ResidentStatus } from '@lmc/ts-common'
import { Avatar, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Tooltip } from 'antd'
import { getNominative } from 'common/utils'
import useFeatureFlag from 'hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import DeactivatedIcon from '../RotaBuilder/common/components/icons/DeactivatedIcon'

type ServiceUserProps = {
  resident: ResidentDtos
}

const ServiceUser = ({ resident }: ServiceUserProps) => {
  const { t } = useTranslation()

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  const flags = useFeatureFlag({
    flagNames: ['rostering_change_management']
  })?.data

  const flagsRosteringChangeManagementEnabled =
    flags?.rostering_change_management?.enabled

  if (!resident) return null

  return (
    <Container>
      <StyledAvatar url={resident.picture} />
      <NameText>{`${resident.name?.first} ${resident.name?.last}`}</NameText>
      {flagsRosteringChangeManagementEnabled &&
        resident.status !== ResidentStatus.active && (
          <Tooltip
            color={theme.color.neutral.color3}
            placement="bottomLeft"
            title={
              <TooltipTitle>
                {t('SU_DEACTIVATED_TOOLTIP', {
                  nominative: residentNominative,
                  residentStatus: resident.status
                })}
              </TooltipTitle>
            }
          >
            <IconWrapper>
              <DeactivatedIcon />
            </IconWrapper>
          </Tooltip>
        )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const NameText = styled(Text)`
  white-space: nowrap;
`

const StyledAvatar = styled(Avatar)`
  border: 0.5px solid ${(props) => props.theme.color.neutral.color7};
`
const TooltipTitle = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`
const IconWrapper = styled.div`
  width: 14px;
  height: 24px;
  &:hover {
    color: ${(props) => props.theme.color.hover.primary};
  }
`

export default ServiceUser
